import React from "react";
import QuestionRenderer from "./questions/QuestionRenderer";

/**
 * Grouped ministry-reference fields (name, role, org, relationship, email, phone).
 *
 * @param {Object} props
 * @param {string} props.title
 * @param {object[]} props.questions
 * @param {Record<string, unknown>} props.answers
 * @param {function(string, unknown, {{ immediate?: boolean }}): void} props.onChange
 * @param {Record<string, string>} [props.errors]
 * @param {Record<string, boolean>} [props.showRequired]
 * @param {boolean} [props.readOnly]
 * @returns {JSX.Element}
 */
export default function ReferenceInput({
  title,
  questions,
  answers,
  onChange,
  errors = {},
  showRequired = {},
  readOnly = false,
}) {
  const byId = Object.fromEntries((questions || []).map((question) => [question.id, question]));
  const prefix = questions?.[0]?.id?.split(".")[0] || "ref";
  const orderedIds = [
    `${prefix}.name`,
    `${prefix}.role`,
    `${prefix}.org`,
    `${prefix}.relationship`,
    `${prefix}.email`,
    `${prefix}.phone`,
  ];
  const extras = (questions || []).filter((question) => !orderedIds.includes(question.id));

  /**
   * @param {string} questionId
   * @param {string} [gridClass]
   * @returns {JSX.Element|null}
   */
  const renderField = (questionId, gridClass = "") => {
    const question = byId[questionId];
    if (!question) return null;
    return (
      <div key={questionId} id={`survey-block-${questionId}`} className={gridClass}>
        <QuestionRenderer
          question={question}
          value={(answers || {})[questionId]}
          error={errors[questionId] || ""}
          showRequired={Boolean(showRequired[questionId])}
          readOnly={readOnly}
          onChange={(next, meta) => onChange(questionId, next, meta)}
        />
      </div>
    );
  };

  return (
    <section
      className="rounded-lg border border-border bg-tertiary p-4 sm:p-5"
      aria-labelledby={`${prefix}-heading`}
    >
      <div className="mb-4">
        <h3 id={`${prefix}-heading`} className="text-lg font-semibold text-text-primary">
          {title}
        </h3>
        <p className="mt-1 text-sm text-text-secondary">
          Name, role, and contact for someone who can confirm your ministry.
        </p>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {renderField(`${prefix}.name`)}
        {renderField(`${prefix}.role`)}
        {renderField(`${prefix}.org`, "md:col-span-2")}
        {renderField(`${prefix}.relationship`, "md:col-span-2")}
        {renderField(`${prefix}.email`)}
        {renderField(`${prefix}.phone`)}
        {extras.map((question) => renderField(question.id, "md:col-span-2"))}
      </div>
    </section>
  );
}
